import { Link } from 'react-router-dom';
import { Fragment, type ReactNode } from 'react';

import { ChevronRightIcon } from '@/components/icons';
import { cn } from '@/lib/cn';

/**
 * Breadcrumb — navegação contextual no header da page (R5.1 Bloco C).
 *
 * Renderiza hierarquia tipo `Manutenção / Preventiva / OS #123`. Cada item
 * pode ser link (`to` setado) ou plain text (último item geralmente).
 *
 * Mobile: encolhe pra mostrar só o último item + dropdown back. Por ora
 * versão simples — vai virar dropdown adaptativo se virar dor.
 */

export interface BreadcrumbItem {
  /** Texto visível. */
  label: string;
  /** Se setado, renderiza como Link. Se null/undefined, texto puro. */
  to?: string | null;
  /** Ícone opcional à esquerda do label. */
  icon?: ReactNode;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  /** Classes extras pro container. */
  className?: string;
}

export function Breadcrumb({ items, className }: BreadcrumbProps) {
  if (items.length === 0) return null;
  return (
    <nav
      aria-label="Breadcrumb"
      data-testid="breadcrumb"
      className={cn(
        'flex items-center gap-1 text-xs text-ink-3 dark:text-ink-dark-3',
        className
      )}
    >
      <ol className="flex items-center gap-1 flex-wrap">
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          return (
            <Fragment key={`${item.label}-${idx}`}>
              <li className="flex items-center gap-1">
                {item.icon && (
                  <span className="opacity-70 [&_svg]:w-3 [&_svg]:h-3">{item.icon}</span>
                )}
                {item.to && !isLast ? (
                  <Link
                    to={item.to}
                    data-testid={`breadcrumb-link-${idx}`}
                    className="hover:text-ink-1 dark:hover:text-ink-dark-1 transition-colors"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span
                    data-testid={`breadcrumb-text-${idx}`}
                    aria-current={isLast ? 'page' : undefined}
                    className={cn(isLast && 'text-ink-1 dark:text-ink-dark-1 font-medium')}
                  >
                    {item.label}
                  </span>
                )}
              </li>
              {!isLast && (
                <li aria-hidden="true">
                  <ChevronRightIcon className="w-3 h-3 opacity-50" />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
